import { nil, NO, UIColor, UITextView, UIView, YES } from "uicore-ts"
import { RHFocusManager } from "../../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "../../Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHCheckbox } from "../../Custom components/RH/RHCheckbox"
import {
    RHButtons,
    RHColors,
    RHFont,
    RHForms,
    RHSpacing
} from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHCompanyAdministrationViewTokens } from "../../Custom components/RH/Utils_slop/RHViewTokens"
import {
    RHCompanyMember,
    RHCompanyMembershipRequestRole
} from "../../SocketClientFunctions"
import { RHCompanyMemberPresentation } from "./CompanyMemberPresentation"


export type RHCompanyManagedMembershipRole = Exclude<RHCompanyMembershipRequestRole, "auditor"> | "consultant"


export class MemberManagementDialogView extends UIView {

    readonly titleLabel = new UITextView(nil, UITextView.type.paragraph)
        .configuredWithObject({
            textColor: RHColors.textHeading,
            style: {
                fontSize: RHFont.sizeLarge,
                fontWeight: RHFont.weightSemibold
            }
        })
        .addedAsSubviewToView(this)

    readonly emailLabel = new UITextView()
        .configuredWithObject({
            textColor: RHColors.textSecondary,
            isSingleLine: YES,
            style: { fontSize: RHFont.sizeSmall }
        })
        .addedAsSubviewToView(this)

    readonly rolesLabel = new UITextView()
        .configuredWithObject({
            text: "Rollid ettevõttes",
            textColor: RHColors.textHeading,
            style: { fontWeight: RHFont.weightSemibold }
        })
        .addedAsSubviewToView(this)

    readonly administratorRequirementLabel = new UITextView()
        .configuredWithObject({
            text: "Ettevõttel peab olema vähemalt üks peakasutaja.",
            textColor: RHColors.warningAmberText,
            isSingleLine: NO,
            style: { fontSize: RHFont.sizeSmall }
        })
        .addedAsSubviewToView(this)

    readonly administratorCheckbox = new RHCheckbox()
        .configuredWithObject({
            titleLabel: {
                text: RHCompanyAdministrationViewTokens.roleDisplayLabels.administrator
            }
        })
        .addedAsSubviewToView(this)

    readonly administratorExplanationLabel = new UITextView()
        .configuredWithObject({
            textColor: RHColors.textSecondary,
            isSingleLine: NO,
            style: { fontSize: RHFont.sizeSmall, fontWeight: RHFont.weightRegular }
        })
        .addedAsSubviewToView(this)

    readonly procurerCheckbox = new RHCheckbox()
        .configuredWithObject({
            titleLabel: {
                text: RHCompanyAdministrationViewTokens.roleDisplayLabels.procurer
            }
        })
        .addedAsSubviewToView(this)

    readonly procurerIneligibleLabel = new UITextView()
        .configuredWithObject({
            text: RHCompanyAdministrationViewTokens.procurerIneligibleExplanation,
            textColor: RHColors.warningAmberText,
            isSingleLine: NO,
            style: { fontSize: RHFont.sizeSmall }
        })
        .addedAsSubviewToView(this)

    readonly offererCheckbox = new RHCheckbox()
        .configuredWithObject({
            titleLabel: {
                text: RHCompanyAdministrationViewTokens.roleDisplayLabels.offerer
            }
        })
        .addedAsSubviewToView(this)

    readonly auditorCheckbox = new RHCheckbox()
        .configuredWithObject({
            titleLabel: {
                text: RHCompanyAdministrationViewTokens.roleDisplayLabels.auditor
            },
            enabled: NO
        })
        .addedAsSubviewToView(this)

    readonly cancelButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Sulge", style: { fontSize: RHButtons.compactFontSize } },
            variant: RHButtons.destructiveUtilityVariant,
            contentPadding: RHButtons.compactContentPadding
        })
        .addedAsSubviewToView(this)

    readonly saveButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Salvesta", style: { fontSize: RHButtons.compactFontSize } },
            variant: "success",
            contentPadding: RHButtons.compactContentPadding
        })
        .addedAsSubviewToView(this)

    override _focusManager = new RHFocusManager(() => {
        if (this._isAuditor) {
            return [this.cancelButton]
        }
        return [
            this.administratorCheckbox,
            this.procurerCheckbox,
            this.offererCheckbox,
            this.cancelButton,
            this.saveButton
        ]
    })

    member?: RHCompanyMember
    _isAuditor = NO
    _hasConsultantRole = NO
    _preventsAdministratorRemoval = NO
    _isEligibleToActAsProcurer = YES


    constructor(elementID?: string) {
        super(elementID)
        this.backgroundColor = UIColor.whiteColor
        this.cancelButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.dialogView?.dismiss()
        }
        this.administratorCheckbox.controlEventTargetAccumulator.SelectionChange = () => {
            this._applyRoleSelectionRules()
        }
    }


    override wasAddedToViewTree() {
        super.wasAddedToViewTree()
        RHPageFocusManager.shared.pushManagerAsNewLayer(this._focusManager!)
    }


    override wasRemovedFromViewTree() {
        super.wasRemovedFromViewTree()
        RHPageFocusManager.shared.popLayer()
    }


    configureForMember(
        member: RHCompanyMember,
        preventsAdministratorRemoval: boolean = NO,
        isEligibleToActAsProcurer: boolean = YES
    ) {
        this.member = member
        const identityPresentation =
            RHCompanyMemberPresentation.identityPresentationForPerson(member)
        this._isAuditor = member.roles.contains("auditor")
        this._hasConsultantRole = member.roles.contains("consultant")
        this._preventsAdministratorRemoval =
            preventsAdministratorRemoval && member.roles.contains("administrator")
        this._isEligibleToActAsProcurer = isEligibleToActAsProcurer

        this.administratorCheckbox.enabled = !this._preventsAdministratorRemoval
        this.administratorExplanationLabel.text =
            RHCompanyMemberPresentation.administratorRoleExplanationForProcurerEligibility(
                isEligibleToActAsProcurer,
                YES
            )
        if (!isEligibleToActAsProcurer) {
            this.procurerCheckbox.hoverText = RHCompanyAdministrationViewTokens.procurerIneligibleExplanation
        }
        
        const roleEditingViews = [
            this.administratorCheckbox,
            this.administratorExplanationLabel,
            this.procurerCheckbox,
            this.offererCheckbox,
            this.saveButton
        ]
        if (this._isAuditor) {
            roleEditingViews.everyElement.removeFromSuperview()
            this.auditorCheckbox.addedAsSubviewToView(this)
        }
        else {
            roleEditingViews.everyElement.addedAsSubviewToView(this)
            this.auditorCheckbox.removeFromSuperview()
        }
        if (!this._isAuditor && this._preventsAdministratorRemoval) {
            this.administratorRequirementLabel.addedAsSubviewToView(this)
        }
        else {
            this.administratorRequirementLabel.removeFromSuperview()
        }
        if (!this._isAuditor && !isEligibleToActAsProcurer) {
            this.procurerIneligibleLabel.addedAsSubviewToView(this)
        }
        else {
            this.procurerIneligibleLabel.removeFromSuperview()
        }

        let titlePrefix = "Halda liiget"
        if (this._isAuditor) {
            titlePrefix = "Vaata liiget"
        }
        this.titleLabel.text = titlePrefix
        if (identityPresentation.escapedDisplayName) {
            this.titleLabel.text += `: ${identityPresentation.escapedDisplayName}`
        }
        this.emailLabel.text = identityPresentation.escapedEmail

        if (this._isAuditor) {
            this.auditorCheckbox.selected = YES
            this.rolesLabel.text = "Audiitori rolli selles vaates ei muudeta."
        }
        else {
            this.rolesLabel.text = "Rollid ettevõttes"
            this.administratorCheckbox.selected = member.roles.contains("administrator")
            this.procurerCheckbox.selected = member.roles.contains("procurer")
            this.offererCheckbox.selected = member.roles.contains("offerer")
            this._applyRoleSelectionRules()
        }

        this.setNeedsLayout()
    }


    _applyRoleSelectionRules() {
        const administratorSelected = this.administratorCheckbox.selected
        if (administratorSelected && this._isEligibleToActAsProcurer) {
            this.procurerCheckbox.selected = YES
        }
        if (administratorSelected) {
            this.offererCheckbox.selected = YES
        }
        this.procurerCheckbox.enabled = !administratorSelected && this._isEligibleToActAsProcurer
        this.offererCheckbox.enabled = !administratorSelected
        if (!this._isEligibleToActAsProcurer) {
            this.procurerCheckbox.selected = NO
        }
    }


    get selectedRoles(): RHCompanyManagedMembershipRole[] {
        const roles: RHCompanyManagedMembershipRole[] = []
        if (this.administratorCheckbox.selected) {
            roles.push("administrator")
        }
        else {
            if (this.procurerCheckbox.selected) {
                roles.push("procurer")
            }
            if (this.offererCheckbox.selected) {
                roles.push("offerer")
            }
        }
        if (this._hasConsultantRole) {
            roles.push("consultant")
        }
        return roles
    }


    override layoutSubviews() {
        super.layoutSubviews()
        const bounds = this.contentBoundsWithInset(this.core.paddingLength)

        bounds
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextRow(RHSpacing.tiny, this.emailLabel)
            .assignedAsFrameOfView(this.emailLabel)
            .rectangleForNextRow(RHSpacing.large, this.rolesLabel)
            .assignedAsFrameOfView(this.rolesLabel)
            .IF(this._isAuditor)
            .rectangleForNextRow(RHSpacing.small, RHForms.fieldSingleLineFieldHeight)
            .assignedAsFrameOfView(this.auditorCheckbox)
            .rectangleForNextRow(RHSpacing.large, RHButtons.compactHeight)
            .rectangleWithWidth(
                RHCompanyAdministrationViewTokens.employeeDialogSaveButtonWidth,
                0.5
            )
            .assignedAsFrameOfView(this.cancelButton)
            .ELSE()
            .rectangleForNextRow(RHSpacing.small, RHForms.fieldSingleLineFieldHeight)
            .assignedAsFrameOfView(this.administratorCheckbox)
            .rectangleForNextRow(RHSpacing.tiny, this.administratorExplanationLabel)
            .assignedAsFrameOfView(this.administratorExplanationLabel)
            .IF(this._preventsAdministratorRemoval)
            .rectangleForNextRow(RHSpacing.tiny, this.administratorRequirementLabel)
            .assignedAsFrameOfView(this.administratorRequirementLabel)
            .ENDIF()
            .rectangleForNextRow(RHSpacing.tiny, RHForms.fieldSingleLineFieldHeight)
            .assignedAsFrameOfView(this.procurerCheckbox)
            .IF(!this._isEligibleToActAsProcurer)
            .rectangleForNextRow(RHSpacing.tiny, this.procurerIneligibleLabel)
            .assignedAsFrameOfView(this.procurerIneligibleLabel)
            .ENDIF()
            .rectangleForNextRow(RHSpacing.tiny, RHForms.fieldSingleLineFieldHeight)
            .assignedAsFrameOfView(this.offererCheckbox)
            .rectangleForNextRow(RHSpacing.large, RHButtons.compactHeight)
            .distributeViewsEquallyAlongWidth(
                [this.cancelButton, this.saveButton],
                RHSpacing.medium
            )
            .ENDIF()
    }


    override intrinsicContentWidth() {
        return 520
    }


}
